/**
 * RunEvidenceSection — the run-record page's Evidence section (26742).
 *
 * Two tabs:
 *   "For the Servicer"    — a condensed narrative (one line per decision,
 *                           no interactive chrome) plus the artifacts a
 *                           Servicer actually cares about (rich variant,
 *                           CSV exports already excluded by the caller).
 *   "All · chronological" — every declared chronology row, each tagged with
 *                           who acted on it (Servicer vs ora) via
 *                           `actorForStep`.
 *
 * Deviation from the original task wording (recorded here, not silently):
 * the substrate does not yet emit a matched/picked/produced/sent taxonomy for
 * decisions — there is no before/after on HITL payloads today — so "For the Servicer" renders the
 * same `decisionLines` the page's own Decisions section is built from, as a
 * condensed one-line-per-decision narrative, rather than four distinct named
 * sub-narratives. It deliberately does NOT reuse `RunDecisionsSection`
 * itself here — that would render the identical interactive list (lightbox
 * buttons, source-lost banners, dimming) TWICE on one page. This is the
 * smallest faithful alternative: everything the taxonomy would have
 * described is still visible, just not pre-sorted into four labeled buckets
 * the data cannot honestly support yet, and not duplicated.
 *
 * The "All · chronological" tab reuses `RunStepsLog` — the CONTAINER, not the
 * `DetailedRunLog` leaf. It originally hand-rolled its own ordered list
 * because the leaf had no per-row decoration slot; `renderRowPrefix` now
 * supplies that, optional and defaulted, so existing callers render
 * byte-identical markup.
 *
 * Going through the container rather than the leaf is deliberate and is the
 * lesson RunStepsLog's own docstring records: ora and the plugin once each
 * assembled chronology/manifest/currentStepId themselves and the SAME run
 * rendered 62 rows in one host and 45 all-green rows in the other. Sharing the
 * leaf is not parity — the input derivation drifts. So this takes the run
 * inputs as a bundle and lets the container derive, which also means the
 * record gains the manifest-aware never-run rows (skipped / not reached /
 * upcoming) that the hand-rolled list never had.
 */
import { useState } from "react";
import {
  WorkflowArtifactsSection,
  isLightboxViewable,
} from "../components/WorkflowArtifactsSection";
import type { WorkflowArtifact } from "../components/WorkflowArtifactsSection";
import ArtifactLightbox from "../components/ArtifactLightbox";
import type { HitlEvent } from "../components/WorkflowHitlHistorySection";
import { RunStepsLog } from "../drawer/sections/RunStepsLog";
import type { RunStepsLogProps } from "../drawer/sections/RunStepsLog";
import { PhaseMilestoneStream } from "../drawer/sections/PhaseMilestoneStream";
import { buildPhaseStream } from "../phaseModel";
import type { PhaseManifest } from "../phaseModel";
import { actorForStep, type StepActor } from "./evidenceViews";
import type { DecisionLine } from "./decisionLines";

/** The run inputs the chronological view needs, minus the presentation props
 *  this component supplies itself. Passed as a BUNDLE rather than a derived
 *  `chronology` array so the manifest resolution stays inside `RunStepsLog` —
 *  see that file on why sharing the leaf component was not enough. */
export type RunEvidenceStepsInput = Omit<
  RunStepsLogProps,
  "renderRowPrefix" | "title" | "defaultExpanded" | "persistKey"
>;

export interface RunEvidenceSectionProps {
  artifacts: WorkflowArtifact[];
  /** Whether the host is still fetching artifacts. Without this the record
   *  renders "no artifacts" while they are still in flight, which reads as a
   *  finished answer — the legacy drawer body showed a skeleton here and
   *  losing it was a real regression, not a simplification. */
  artifactsLoading?: boolean;
  decisionLines: DecisionLine[];
  runSteps: RunEvidenceStepsInput;
  hitlEvents: HitlEvent[];
  /** All-details view: the chronology is rendered at STEP level. The Servicer
   *  view collapses it to the milestone summary — 4 phases and 11 milestones
   *  read as a story, 51 steps read as a log. */
  allDetails?: boolean;
  /** The declared phase/milestone manifest. Separate from `runSteps`'
   *  catalogEntry because the two are different VIEWS of the host's catalog
   *  object — `PhaseManifest` for the milestone stream, `StepCatalogEntryLike`
   *  for the step log. Absent (the record page, which holds no catalog yet)
   *  degrades to no summary rather than a wrong one. */
  phaseManifest?: PhaseManifest | null;
  /** Whether to render the milestone summary at all. FALSE inside the drawer:
   *  the phase stream is already the primary body's hero there, and a second
   *  copy inside the record would be the same run twice on one screen. On the
   *  standalone page there is no primary body, so the record owns it. */
  showMilestoneSummary?: boolean;
}

function ActorChip({ actor }: { actor: StepActor }) {
  const isServicer = actor === "servicer";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-semibold flex-shrink-0 ${
        isServicer
          ? "bg-primary-success-50 text-primary-success-700"
          : "bg-primary-gray-light-100 text-primary-gray-light-600"
      }`}
      data-testid="evidence-actor-chip"
      data-actor={actor}
    >
      {isServicer ? "Servicer" : "ora"}
    </span>
  );
}

/** Condensed, non-interactive narrative for the "For the Servicer" tab — one
 *  line per decision, no lightbox/banner chrome (that lives in the page's
 *  own Decisions section). */
function ServicerNarrative({ lines }: { lines: DecisionLine[] }) {
  if (lines.length === 0) {
    return (
      <p
        className="text-xs text-primary-gray-light-600"
        data-testid="run-evidence-narrative-empty"
      >
        No narrative is available for this run yet.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1" data-testid="run-evidence-narrative-list">
      {lines.map((line) => (
        <li
          key={line.key}
          className={`text-xs text-primary-gray-light-800 ${line.dimmed ? "opacity-50" : ""}`}
          data-testid="run-evidence-narrative-line"
        >
          {line.summary}
        </li>
      ))}
    </ul>
  );
}

/** The Servicer's chronology: phases and milestones, not steps. Rendered from
 *  the SAME run inputs the step log uses, through the shared builder — so the
 *  two levels cannot disagree about what happened.
 *
 *  `buildPhaseStream` is called exactly ONCE per render here: it emits a
 *  fallback telemetry event for a pre-model instance, and a second call would
 *  double-count it. */
function MilestoneSummary({
  runSteps,
  manifest,
}: {
  runSteps: RunEvidenceStepsInput;
  manifest: PhaseManifest | null;
}) {
  const model = buildPhaseStream(runSteps.status ?? null, manifest);
  if (!model) return null;
  return (
    <div data-testid="run-evidence-milestone-summary">
      <PhaseMilestoneStream model={model} />
    </div>
  );
}

/** The chronological view — `DetailedRunLog`, decorated with an actor chip per
 *  row. This used to be a hand-rolled `<ol>` here, duplicating a component the
 *  drawer already had; the only thing that had been missing was a per-row
 *  decoration slot, which `DetailedRunLog` now takes. Its three honest
 *  absence states (null / [] / declared-but-never-run) come along for free and
 *  are no longer maintained in two places. */
function ChronologicalList({
  runSteps,
  hitlEvents,
}: {
  runSteps: RunEvidenceStepsInput;
  hitlEvents: HitlEvent[];
  /** All-details view: the chronology is rendered at STEP level. The Servicer
   *  view collapses it to the milestone summary — 4 phases and 11 milestones
   *  read as a story, 51 steps read as a log. */
  allDetails?: boolean;
  /** The declared phase/milestone manifest. Separate from `runSteps`'
   *  catalogEntry because the two are different VIEWS of the host's catalog
   *  object — `PhaseManifest` for the milestone stream, `StepCatalogEntryLike`
   *  for the step log. Absent (the record page, which holds no catalog yet)
   *  degrades to no summary rather than a wrong one. */
  phaseManifest?: PhaseManifest | null;
  /** Whether to render the milestone summary at all. FALSE inside the drawer:
   *  the phase stream is already the primary body's hero there, and a second
   *  copy inside the record would be the same run twice on one screen. On the
   *  standalone page there is no primary body, so the record owns it. */
  showMilestoneSummary?: boolean;
}) {
  return (
    <RunStepsLog
      {...runSteps}
      bare
      renderRowPrefix={(row) => (
        <ActorChip actor={actorForStep(row.stepId, null, hitlEvents)} />
      )}
    />
  );
}

export function RunEvidenceSection({
  artifacts,
  artifactsLoading = false,
  decisionLines,
  runSteps,
  hitlEvents,
  allDetails = false,
  phaseManifest = null,
  showMilestoneSummary = false,
}: RunEvidenceSectionProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const viewable = artifacts.filter(isLightboxViewable);

  return (
    <div className="flex flex-col gap-3" data-testid="run-evidence-section">
      {/* The timeline first, the images under it. One order in both views —
          the level of the timeline is what changes, not the layout. */}
      {allDetails ? (
        <div data-testid="run-evidence-all-panel">
          <ChronologicalList runSteps={runSteps} hitlEvents={hitlEvents} />
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="run-evidence-servicer-panel">
          <ServicerNarrative lines={decisionLines} />
          {showMilestoneSummary ? (
            <MilestoneSummary runSteps={runSteps} manifest={phaseManifest} />
          ) : null}
        </div>
      )}

      <WorkflowArtifactsSection
        artifacts={artifacts}
        loading={artifactsLoading}
        variant="rich"
        heading="What ora produced"
        onArtifactClick={
          viewable.length > 0
            ? (i) => {
                const clicked = artifacts[i];
                const idx = viewable.findIndex((a) => a.url === clicked.url);
                if (idx >= 0) setLightboxIndex(idx);
              }
            : undefined
        }
      />

      <ArtifactLightbox
        open={lightboxIndex !== null}
        artifacts={viewable}
        startIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
      />
    </div>
  );
}

export default RunEvidenceSection;
